import { ethers } from 'ethers';
import { POD_PORTAL_ADMIN_ABI } from '../contracts/pod';
import { getChainConfig, getRpcUrlsForChain } from './registry';
import { fetchPodOracleTokenUsdPrice } from './podPriceOracle';
import { logger } from '../logger';
import type { ChainConfig, TokenConfig } from './types';
import type { BridgeData } from '../hooks/useBridgeData';
import type { SimulationResult } from '../hooks/useBridgeFees';

const ERC20_BALANCE_ABI = ['function balanceOf(address) view returns (uint256)'];

/** Same percentage scale as the COTI bridges (500 bps = 0.05%). */
const FEE_DIVISOR = 1_000_000n;

/** Deployed portals use uint128.max as the "no max fee cap" sentinel. */
export const POD_NO_MAX_FEE_SENTINEL = (1n << 128n) - 1n;

/** Formats a fee config value, mapping the no-cap sentinel to "0" (COTI convention). */
const formatFee = (value: bigint): string =>
  value >= POD_NO_MAX_FEE_SENTINEL ? '0' : ethers.formatEther(value);

async function fetchPortalRow(
  token: TokenConfig,
  config: ChainConfig,
  provider: ethers.JsonRpcProvider,
  nativeSymbol: string,
): Promise<BridgeData> {
  const portalAddress = config.addresses[token.bridgeAddressKey!];
  const privateToken = config.tokens.find(
    t => t.isPrivate && t.bridgeAddressKey === token.bridgeAddressKey
  );
  const base = {
    bridgeName: `${token.symbol} PoD Portal`,
    bridgeAddress: portalAddress,
    publicToken: token.symbol,
    publicTokenIcon: token.icon,
    privateToken: privateToken?.symbol || 'N/A',
    privateTokenIcon: privateToken?.icon || '',
    tokenDecimals: token.decimals,
    feeTokenSymbol: nativeSymbol,
    minDepositAmount: 'N/A',
    maxDepositAmount: 'N/A',
    minWithdrawAmount: 'N/A',
    maxWithdrawAmount: 'N/A',
    accumulatedFees: '0',
    nativeCotiFee: '0',
    isPaused: false,
    isDepositEnabled: true,
    isLoading: false,
  };

  try {
    const portal = new ethers.Contract(portalAddress, POD_PORTAL_ADMIN_ABI, provider);
    // Do not swallow collateral balance failures as 0 — a failed balanceOf/getBalance
    // must fail the row so admins never see a false empty collateral reading.
    const collateralBalancePromise =
      token.addressKey && config.addresses[token.addressKey]
        ? new ethers.Contract(config.addresses[token.addressKey], ERC20_BALANCE_ABI, provider)
            .balanceOf(portalAddress)
        : token.isNative
          ? provider.getBalance(portalAddress)
          : Promise.resolve(0n);

    const [depCfg, wdCfg, accFees, balance, paused, depositEnabled, minDep, maxDep, minWd, maxWd] =
      await Promise.all([
        portal.getFeeConfig(true),
        portal.getFeeConfig(false),
        portal.accumulatedPortalFees().catch(() => 0n),
        // Native-wrapped portals (ETH/AVAX) hold WETH/WAVAX collateral, not native coin.
        collateralBalancePromise,
        portal.paused().catch(() => false),
        portal.isDepositEnabled().catch(() => true),
        portal.minDepositAmount().catch(() => null),
        portal.maxDepositAmount().catch(() => null),
        portal.minWithdrawAmount().catch(() => null),
        portal.maxWithdrawAmount().catch(() => null),
      ]);
    // Max limits use the same no-cap sentinel family as fee maxFee (uint128.max+).
    // Map those to "0" (COTI / admin-UI convention for "no cap"), not a huge decimal.
    // Keep raw decimals here — UI layers use formatAmountLimitDisplay for labels.
    const formatLimit = (value: bigint | null, isMax = false): string => {
      if (value == null) return 'N/A';
      if (isMax && value >= POD_NO_MAX_FEE_SENTINEL) return '0';
      return ethers.formatUnits(value, token.decimals);
    };
    return {
      ...base,
      depositFixedFee: formatFee(depCfg[0]),
      depositPercentageBps: depCfg[1].toString(),
      depositMaxFee: formatFee(depCfg[2]),
      withdrawFixedFee: formatFee(wdCfg[0]),
      withdrawPercentageBps: wdCfg[1].toString(),
      withdrawMaxFee: formatFee(wdCfg[2]),
      accumulatedCotiFees: ethers.formatEther(accFees),
      bridgeBalance: ethers.formatUnits(balance, token.decimals),
      isPaused: Boolean(paused),
      isDepositEnabled: Boolean(depositEnabled),
      minDepositAmount: formatLimit(minDep),
      maxDepositAmount: formatLimit(maxDep, true),
      minWithdrawAmount: formatLimit(minWd),
      maxWithdrawAmount: formatLimit(maxWd, true),
      error: null,
    };
  } catch (err) {
    logger.error(`Error fetching PoD portal data for ${token.symbol}:`, err);
    return {
      ...base,
      depositFixedFee: 'Error',
      depositPercentageBps: 'Error',
      depositMaxFee: 'Error',
      withdrawFixedFee: 'Error',
      withdrawPercentageBps: 'Error',
      withdrawMaxFee: 'Error',
      accumulatedCotiFees: '0',
      bridgeBalance: '0',
      error: 'Failed to fetch portal data',
    };
  }
}

/**
 * Live backoffice reader for PoD privacy portals (Sepolia/Fuji).
 * Reads the deposit/withdraw fee configs, accumulated portal fees and portal
 * balance for every public token with a configured portal, shaped as
 * {@link BridgeData} rows. Fee values are native-coin denominated; the row's
 * `feeTokenSymbol` carries the display symbol (ETH/AVAX).
 */
export async function fetchPodBridgeData(chainId: number): Promise<BridgeData[]> {
  const config = getChainConfig(chainId);
  if (!config || config.portalStrategy !== 'pod-privacy-portal') return [];
  const nativeSymbol = config.walletNetwork.nativeCurrency.symbol;
  const publicTokens = config.tokens.filter(
    t => !t.isPrivate && t.bridgeAddressKey && config.addresses[t.bridgeAddressKey]
  );

  let lastError: unknown;
  for (const rpcUrl of getRpcUrlsForChain(chainId)) {
    const provider = new ethers.JsonRpcProvider(rpcUrl);
    try {
      await provider.getBlockNumber(); // probe before fanning out
      return await Promise.all(
        publicTokens.map(token => fetchPortalRow(token, config, provider, nativeSymbol))
      );
    } catch (err) {
      lastError = err;
    }
  }
  logger.error(`Error fetching PoD bridge data for chain ${chainId}:`, lastError);
  return [];
}

/**
 * Local mirror of the assumed PoD portal fee math for the backoffice
 * simulate panel: max(fixedFee, valueInNative * bps / 1e6), capped at maxFee
 * (0 = no cap). ERC-20 amounts are converted to native units via the chain's
 * PoDPriceOracle live prices.
 *
 * @param chainId       - PoD chain id (Sepolia 11155111 / Fuji 43113)
 * @param tokenSymbol   - Public token symbol from the chain config (e.g. "ETH", "USDC")
 * @param amount        - Token amount (human-readable)
 * @param fixedFee      - Floor fee in native coin (human-readable)
 * @param percentageBps - Basis points relative to FEE_DIVISOR (raw number string)
 * @param maxFee        - Cap fee in native coin (human-readable, "0" = no cap)
 */
export async function simulatePodPortalFee(
  chainId: number,
  tokenSymbol: string,
  amount: string,
  fixedFee: string,
  percentageBps: string,
  maxFee: string,
): Promise<SimulationResult> {
  try {
    const config = getChainConfig(chainId);
    if (!config) return { fee: '—', explanation: 'Unsupported chain' };
    const token = config.tokens.find(t => t.symbol === tokenSymbol && !t.isPrivate);
    if (!token) return { fee: '—', explanation: 'Unknown token' };

    // Fees are charged in the native coin; convert ERC-20 amounts via oracle prices.
    const amountWei = ethers.parseUnits(amount, token.decimals);
    let valueInNative: bigint;
    if (token.isNative) {
      valueInNative = amountWei;
    } else {
      const nativeSymbol = config.walletNetwork.nativeCurrency.symbol;
      const [tokenUsd, nativeUsd] = await Promise.all([
        fetchPodOracleTokenUsdPrice(tokenSymbol, chainId),
        fetchPodOracleTokenUsdPrice(nativeSymbol, chainId),
      ]);
      if (!tokenUsd || !nativeUsd) {
        // Mirrors the portal: without a live oracle price it skips dynamic
        // pricing and charges the fixed fee (verified on Fuji MTT:
        // estimateDepositFees returns fixedFee with usedDynamicPricing=false).
        const fixed = ethers.parseEther(fixedFee || '0');
        const cap = ethers.parseEther(maxFee || '0');
        const fee = cap > 0n && fixed > cap ? cap : fixed;
        return {
          fee: parseFloat(ethers.formatEther(fee)).toFixed(6),
          explanation: 'Fixed fee applied (no live oracle price)',
        };
      }
      const tokenUsdWei = ethers.parseEther(tokenUsd.toString());
      const nativeUsdWei = ethers.parseEther(nativeUsd.toString());
      const amount18 = amountWei * 10n ** BigInt(18 - token.decimals);
      valueInNative = (amount18 * tokenUsdWei) / nativeUsdWei;
    }

    const fixedFeeWei = ethers.parseEther(fixedFee || '0');
    const maxFeeWei = ethers.parseEther(maxFee || '0');
    const bps = BigInt(parseInt(percentageBps || '0', 10));

    const percentageFee = (valueInNative * bps) / FEE_DIVISOR;
    let fee = percentageFee > fixedFeeWei ? percentageFee : fixedFeeWei;
    if (maxFeeWei > 0n && fee > maxFeeWei) fee = maxFeeWei;

    const explanation =
      maxFeeWei > 0n && fee === maxFeeWei && fee !== fixedFeeWei
        ? 'Max fee cap applied'
        : fee === fixedFeeWei
          ? 'Fixed fee floor applied'
          : 'Percentage fee applied';
    return { fee: parseFloat(ethers.formatEther(fee)).toFixed(6), explanation };
  } catch (err) {
    logger.error('simulatePodPortalFee error:', err);
    return { fee: '—', explanation: 'Simulation failed' };
  }
}
